<section class="policy">
  <div class="policy-inner">
    <h1>Privacy Policy for OrderYaa POS</h1>
    <p class="updated">Last updated: {{ lastUpdated }}</p>

    <p>
      This Privacy Policy explains how Tech Colleagues ("we", "us", or "our") collects, uses, stores, and
      protects information when you use the OrderYaa POS mobile application (the "App"). By downloading or
      using the App, you agree to the practices described in this policy.
    </p>

    <p>
      OrderYaa POS is a business point-of-sale (POS) tool for retail stores, cafes, and small businesses.
      It is intended for use by merchants and their cashiers. The App is not directed to consumers as a
      personal finance product and is not intended for use by children.
    </p>

    <h2>1. Information We Collect</h2>
    <p>
      We only collect the information needed to operate the App and provide point-of-sale services. The
      App collects the following types of data:
    </p>

    <h3>a) Account and login information</h3>
    <ul>
      <li>Store/company login identifier (email address or username)</li>
      <li>Cashier identity and cashier PIN used to sign in</li>
      <li>Authentication tokens generated when you sign in</li>
    </ul>

    <h3>b) Sales and transaction information</h3>
    <ul>
      <li>Products, prices, quantities, discounts, and taxes entered during a sale</li>
      <li>Payment method used (cash, card, mobile wallet, or QR) and amounts</li>
      <li>Cashier session details (opening float, cash reconciliation, session open/close times)</li>
      <li>Receipt and transaction records</li>
    </ul>

    <h3>c) Payment-related information</h3>
    <p>
      We do not collect or store full payment card numbers. Where card or wallet payments are recorded, we
      keep only a reference such as the last four digits of a card or a transaction identifier for receipt
      and reconciliation purposes.
    </p>

    <p class="emphasis">Information we do NOT collect:</p>
    <ul>
      <li>We do not collect your precise or approximate location.</li>
      <li>We do not access your contacts, photos, messages, or calendar.</li>
      <li>We do not collect health information or web browsing history.</li>
      <li>The App does not contain advertising and does not collect data for advertising purposes.</li>
    </ul>

    <h2>2. How We Use Your Information</h2>
    <p>We use the information we collect only for legitimate business purposes, including to:</p>
    <ul>
      <li>Authenticate users and keep accounts and cashier sessions secure</li>
      <li>
        Provide core point-of-sale functionality (browsing products, building carts, applying discounts
        and taxes, and processing checkout)
      </li>
      <li>Record sales, payments, and session activity for the store</li>
      <li>Generate and print receipts</li>
      <li>Keep your product catalog, prices, and sales synchronized with your OrderYaa store account</li>
      <li>Maintain the security, reliability, and proper functioning of the App</li>
      <li>Comply with applicable legal and accounting obligations</li>
    </ul>
    <p>We do not use your information for advertising, and we do not sell your information.</p>

    <h2>3. How Information Is Stored and Transmitted</h2>
    <ul>
      <li>
        <strong>In transit:</strong> All communication between the App and our servers is encrypted using
        secure HTTPS connections.
      </li>
      <li>
        <strong>On the device:</strong> Authentication tokens are kept in the device's encrypted secure
        storage. Operational data (such as catalog and transaction records) may be stored in a local
        database on the device to allow the App to function.
      </li>
      <li>
        <strong>On our servers:</strong> Transaction and account data is sent to and stored on our backend
        systems associated with your OrderYaa store account.
      </li>
    </ul>

    <h2>4. Third-Party Services</h2>
    <p>
      We do not sell or share your personal information with third parties for their own marketing or
      advertising purposes.
    </p>
    <p>
      The App communicates with our own backend API (operated for OrderYaa) to authenticate users and
      synchronize store and sales data. We may use trusted service providers that host or support this
      infrastructure solely to operate the service on our behalf, and only to the extent necessary to
      provide the App's functionality. The App does not include third-party advertising networks or
      social-login providers.
    </p>

    <h2>5. Children's Privacy</h2>
    <p>
      OrderYaa POS is a business tool intended for users 18 years of age and older. It is not directed to
      or designed for children, and we do not knowingly collect personal information from children. If you
      believe a child has provided us with personal information, please contact us so that we can take
      appropriate action to remove it.
    </p>

    <h2>6. Data Security</h2>
    <p>
      We take reasonable technical and organizational measures to protect your information, including
      encryption of data in transit (HTTPS), encrypted storage of authentication tokens on the device, and
      access controls that require login and a cashier PIN. However, no method of transmission or
      electronic storage is completely secure, and we cannot guarantee absolute security.
    </p>

    <h2>7. Data Retention and Deletion</h2>
    <p>
      We retain account and transaction data for as long as your store account is active or as needed to
      provide the service and to meet legal, accounting, and business record-keeping requirements.
    </p>
    <p>
      You may request deletion of your account and associated personal data by emailing us at the address
      in the "Contact Us" section below. We will process verified deletion requests in accordance with
      applicable law, except where we are required to retain certain records (for example, financial or tax
      records).
    </p>

    <h2>8. Your Rights</h2>
    <p>
      Depending on your location, you may have the right to access, correct, or request deletion of your
      personal information, and to object to or restrict certain processing. To exercise these rights,
      please contact us using the details below. We may need to verify your identity before acting on a
      request.
    </p>

    <h2>9. Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. When we make material changes, we will update
      the "Last updated" date at the top of this policy and, where appropriate, provide additional notice.
      Your continued use of the App after changes take effect constitutes acceptance of the updated policy.
    </p>

    <h2>10. Contact Us</h2>
    <p>If you have any questions, concerns, or requests regarding this Privacy Policy or your data, please contact us:</p>
    <ul class="contact-list">
      <li><strong>Developer:</strong> Tech Colleagues</li>
      <li><strong>App:</strong> OrderYaa POS</li>
      <li><strong>Email:</strong> <a href="mailto:hr@techcolleagues.com">hr&#64;techcolleagues.com</a></li>
    </ul>

    <p class="footnote">
      This Privacy Policy is provided to comply with Google Play Store requirements and applicable data
      protection regulations.
    </p>
  </div>
</section>
